import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import WorkLog from "../models/WorkLog";
import WorkLogReport from "../models/WorkLogReport";
import { EnsolversOpsUser } from "../models/EnsolversOpsUser";
import Holiday from "../models/Holiday";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import Position from "../models/Position";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import HolidayService from "../services/HolidayService";
import AssignationService from "../services/AssignationService";
import Assignation from "../models/Assignation";
import PositionService from "../services/PositionService";
import { Country } from "../utils/Countries";
import OpsDateUtils from "../utils/OpsDateUtils";
import WorkLogService from "../services/WorkLogService";
import { QueryKeyFactory, WORKLOG_DAILY_LIST_CONTEXT } from "../services/queryKeys";
import { rethrowWithFallback, unwrap } from "../utils/queryFn";
import { fetchOpsUserByCoreId } from "./useUserInfo";

const workLogKeys = new QueryKeyFactory(WorkLogService.path);

const FIRST_PAGE = 0,
  PAGE_SIZE = 7;

interface UseWorkLogPopupParams {
  report: WorkLogReport;
  date: Date;
  day?: number;
  onWorkLogUpdate: (workLog: WorkLog, prevInternalHours: number) => void;
}

const useWorkLogPopup = ({ report, date, day, onWorkLogUpdate }: UseWorkLogPopupParams) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState({} as EnsolversOpsUser);
  const [holidays, setHolidays] = useState([] as Holiday[]);
  const [position, setPosition] = useState({} as Position);
  const [openForm, setOpenForm] = useState(false);
  const [pagedWorkLogs, setPagedWorkLogs] = useState<PagedResult<WorkLog>>(PagedResult.empty);
  const [fetchedWorkLogs, setFetchedWorkLogs] = useState(false);
  const [workLogForEdit, setWorkLogForEdit] = useState<WorkLog | undefined>(undefined);

  const fetchHolidays = async (): Promise<Holiday[]> => {
    try {
      const month = date.getMonth();
      const country = report.country || Country.ARGENTINA;
      return await unwrap(HolidayService.findHolidaysOfMonthByCountry(month, country));
    } catch (error) {
      NotificationService.error("Error fetching Holidays");
      throw error;
    }
  };

  const fetchUser = (): Promise<EnsolversOpsUser> => fetchOpsUserByCoreId(queryClient, report.coreUserExternalId);

  const fetchAssignation = async (): Promise<Assignation> => {
    try {
      return await unwrap(AssignationService.getByExternalId(report.assignationExternalId));
    } catch (error) {
      NotificationService.error("Error getting user assignation");
      throw error;
    }
  };

  const fetchPosition = async (assignation: Assignation): Promise<Position> => {
    try {
      return await unwrap(PositionService.getByExternalId(assignation.position.externalId!));
    } catch (error) {
      NotificationService.error("Error getting user position");
      throw error;
    }
  };

  const fetchPagedWorklogs = async (): Promise<PagedResult<WorkLog>> => {
    try {
      return await unwrap(
        WorkLogService.searchAll(
          FIRST_PAGE,
          PAGE_SIZE,
          report.assignationExternalId,
          date.getFullYear(),
          date.getMonth(),
          report.coreUserExternalId,
          day,
        ),
      );
    } catch (error) {
      NotificationService.error("Error fetching work logs");
      throw error;
    }
  };

  /**
   * Retrieves data from work logs (WorkLog).
   * If the data has been previously retrieved, it returns the stored information.
   * Otherwise, it makes a request to fetchPagedWorklogs, stores the data, and returns it.
   *
   * @returns A promise that resolves to a PagedResult<WorkLog> object containing work logs.
   */
  const handleFetchWorkLogs = async (): Promise<PagedResult<WorkLog>> => {
    try {
      if (!fetchedWorkLogs) {
        setFetchedWorkLogs(true);
        const fetchedData = await fetchPagedWorklogs();

        const finalPagedResult = PagedResult.fromArray(buildPaddedWeek(fetchedData));
        setPagedWorkLogs(finalPagedResult);
        return finalPagedResult;
      }
      return pagedWorkLogs;
    } catch (error) {
      setFetchedWorkLogs(false);
      throw error;
    }
  };

  const handleOpenForm = async (worklog: WorkLog): Promise<void> => {
    try {
      const [holidaysRes, userRes, assignationRes] = await Promise.all([fetchHolidays(), fetchUser(), fetchAssignation()]);
      setHolidays(holidaysRes);
      setUser(userRes);

      const positionRes = await fetchPosition(assignationRes);
      setPosition(positionRes);

      const isNew = !worklog.externalId;
      const formWorkLog: WorkLog = {
        ...worklog,
        assignationName: assignationRes.externalId!,
        assignationExternalId: assignationRes.externalId!,
        ...(isNew && {
          hours: positionRes.externalHours,
          internalHours: positionRes.internalHours,
        }),
      };

      setWorkLogForEdit(formWorkLog);
      setOpenForm(true);
    } catch (error) {
      console.error("[Handle Open Form] An error occurred:", error);
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (workLogToSave: WorkLog) => {
      try {
        return await unwrap(workLogToSave.externalId ? WorkLogService.update(workLogToSave) : WorkLogService.create(workLogToSave));
      } catch (error) {
        return rethrowWithFallback(error, workLogToSave.externalId ? "Error trying to update." : "WorkLog for that day already exists!");
      }
    },
    onSuccess: async (_saved, workLogToSave) => {
      NotificationService.info(workLogToSave.externalId ? "Worklog updated!" : "Worklog created!");
      await queryClient.invalidateQueries({ queryKey: workLogKeys.lists(WORKLOG_DAILY_LIST_CONTEXT) });
    },
  });

  const saveWorkLog = (workLogToSave: WorkLog) => saveMutation.mutateAsync(workLogToSave);

  const handleUpdateWorkLog = async () => {
    try {
      if (workLogForEdit?.externalId && workLogForEdit.externalId !== "undefined") {
        const prevHours: number = workLogForEdit.hours;
        const updatedWorkLog = await unwrap(WorkLogService.getByExternalId(workLogForEdit.externalId));

        const updatedReport: WorkLog[] = pagedWorkLogs!.content.map((workLog) =>
          workLog.externalId === updatedWorkLog.externalId ? updatedWorkLog : workLog,
        );

        setPagedWorkLogs(PagedResult.fromArray(updatedReport));
        onWorkLogUpdate(updatedWorkLog, prevHours);
      } else {
        const pagedWorklogsData = await fetchPagedWorklogs();

        const updatedWorkLog = pagedWorklogsData.content.find((wl) => OpsDateUtils.isSameCalendarDay(wl.shortDay, workLogForEdit.shortDay));

        if (updatedWorkLog) {
          onWorkLogUpdate(updatedWorkLog, 0);
        }

        setPagedWorkLogs(PagedResult.fromArray(buildPaddedWeek(pagedWorklogsData)));
      }

      setWorkLogForEdit(undefined);
    } catch (error) {
      NotificationService.error("Error updating worklog");
    }
  };

  const buildPaddedWeek = (fetchedData: PagedResult<WorkLog>): WorkLog[] => {
    const startOfWeek = new Date(date.getFullYear(), date.getMonth(), day || 1);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + (6 - startOfWeek.getDay()));
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const actualEnd = endOfWeek > lastDayOfMonth ? lastDayOfMonth : endOfWeek;

    const weekDates: Date[] = [];
    for (let d = new Date(startOfWeek); d <= actualEnd; d.setDate(d.getDate() + 1)) {
      if (!OpsDateUtils.isWeekend(d)) {
        weekDates.push(new Date(d));
      }
    }

    return weekDates.map(
      (weekDate) =>
        fetchedData.content.find((wl) => OpsDateUtils.isSameCalendarDay(wl.shortDay, weekDate)) ??
        ({ shortDay: weekDate, hours: 0, internalHours: 0, description: "" } as WorkLog),
    );
  };

  return {
    user,
    holidays,
    position,
    openForm,
    setOpenForm,
    handleFetchWorkLogs,
    handleUpdateWorkLog,
    saveWorkLog,
    handleOpenForm,
    workLogForEdit,
    setWorkLogForEdit,
  };
};

export default useWorkLogPopup;
